export function isPrimitive(
  value: unknown
): value is string | number | boolean | symbol | bigint | null | undefined {
  return (
    value === null ||
    typeof value === "undefined" ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "symbol" ||
    typeof value === "bigint"
  );
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  // True if created via {} / new Object(), or if it has no prototype (Object.create(null))
  // False if created via class, Array, Map, Set, etc.
  return proto === null || proto === Object.prototype;
}

isRecord.empty = function (value: unknown): value is Record<string, unknown> {
  return isRecord(value) && Object.keys(value).length === 0;
};
isRecord.notEmpty = function (
  value: unknown
): value is Record<string, unknown> {
  return isRecord(value) && Object.keys(value).length > 0;
};

export function isMap(value: unknown): value is Map<unknown, unknown> {
  return value instanceof Map;
}
isMap.empty = function (value: unknown): value is Map<unknown, unknown> {
  return isMap(value) && value.size === 0;
};
isMap.notEmpty = function (value: unknown): value is Map<unknown, unknown> {
  return isMap(value) && value.size > 0;
};

export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}
isArray.empty = function (value: unknown): value is unknown[] {
  return isArray(value) && value.length === 0;
};
isArray.notEmpty = function (value: unknown): value is unknown[] {
  return isArray(value) && value.length > 0;
};
isArray.of = function <T>(
  value: unknown,
  predicate: (item: unknown) => item is T
): value is T[] {
  return isArray(value) && value.every(predicate);
};

export function isSet(value: unknown): value is Set<unknown> {
  return value instanceof Set;
}
isSet.empty = function (value: unknown): value is Set<unknown> {
  return isSet(value) && value.size === 0;
};
isSet.notEmpty = function (value: unknown): value is Set<unknown> {
  return isSet(value) && value.size > 0;
};
isSet.of = function <T>(
  value: unknown,
  predicate: (item: unknown) => item is T
): value is Set<T> {
  return isSet(value) && Array.from(value).every(predicate);
};

export function isString(value: unknown): value is string {
  return typeof value === "string";
}
isString.empty = function (
  value: unknown,
  { trimmed = true } = {}
): value is string {
  return (
    typeof value === "string" &&
    (trimmed ? value.trim().length === 0 : value.length === 0)
  );
};
isString.notEmpty = function (value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
};

export function isNumber(value: unknown): value is number {
  return typeof value === "number";
}
isNumber.valid = function (value: unknown): value is number {
  return isNumber(value) && !Number.isNaN(value);
};
isNumber.invalid = function (value: unknown): value is number {
  return isNumber(value) && Number.isNaN(value);
};

export function isDate(value: unknown): value is Date {
  return value instanceof Date;
}
isDate.valid = function (value: unknown): value is Date {
  return isDate(value) && !Number.isNaN(value.getTime());
};
isDate.invalid = function (value: unknown): value is Date {
  return isDate(value) && Number.isNaN(value.getTime());
};

export function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

export function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}
